import i18n from 'i18next';
import { addDays, parse } from 'date-fns';

import { PassengerState } from '../Checkout/store/passengers/reducers';
import {
	AeroexpressSelectedTrip,
	AeroexpressTrip,
	AviaPassengerType,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	PassengerFragment
} from '@websky/graphql';
import { Money } from '../types';
import { isRoundTrip } from './components/AeroexpressPopup/utils';
import { format } from '../utils';
import { CartServiceGds } from '../Cart/types';
import { ServicePassengerSelected } from '../VipServiceSelect';

interface PassengerPrice {
	type: AviaPassengerType;
	supplierType?: string;
	price: Money;
}

export const getPassengerPricesMap = (
	travellers: PassengerState[],
	services: (
		| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
		| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment
	)[],
	isMinPrice?: boolean
) => {
	const pricesByPassengerMap = new Map<AviaPassengerType, PassengerPrice>();

	services.forEach(service => {
		const allowedPassengerIds = new Set(service.allowedPassengers.reduce((acc, ids) => [...acc, ...ids], []));

		allowedPassengerIds.forEach(passengerId => {
			const traveller = travellers.find(traveller => traveller.id === passengerId);

			if (traveller) {
				const type = traveller.type as AviaPassengerType;
				const supplierType = traveller.supplierTravellerType;

				const priceByPassenger = pricesByPassengerMap.get(type);
				const condition = isMinPrice
					? !priceByPassenger || priceByPassenger?.price?.amount > service.price.amount
					: !priceByPassenger;

				if (condition) {
					pricesByPassengerMap.set(type, { type, supplierType, price: service.price });
				}
			}
		});
	});

	return [...pricesByPassengerMap.values()];
};

export const getAeroexpressTripLabel = (trip: AeroexpressTrip | AeroexpressSelectedTrip) => {
	const departureDate = parse(trip.departureDate, 'HH:mm dd.MM.yyyy', new Date());

	let label = format(departureDate, 'dd.MM.yyyy');

	if (isRoundTrip(trip)) {
		label += ` - ${i18n.t('Aeroexpress:To')} ${format(addDays(departureDate, 30), 'dd.MM.yyyy')}`;
	}

	return label;
};

export const getVipServicesToDelete = (
	payload: CartServiceGds,
	travellers: PassengerFragment[]
): ServicePassengerSelected[] => {
	const removeServices: ServicePassengerSelected[] = [];

	const commonRemoveService: ServicePassengerSelected = {
		serviceId: payload.serviceId,
		passengerId: payload.passengerId,
		count: 0
	};

	if (payload.allSegments) {
		const travellerServicesById = travellers.find(traveller => traveller.id === payload.passengerId)?.services
			?.gdsServices?.services;

		if (travellerServicesById?.length) {
			travellerServicesById.forEach(service => {
				if (service.serviceId === payload.serviceId) {
					removeServices.push({ ...commonRemoveService, segmentIds: service.segmentIds });
				}
			});
		}
	} else {
		removeServices.push({ ...commonRemoveService, segmentIds: [payload.segmentId] });
	}

	return removeServices;
};
